import {useEffect, useState} from 'react';
import type {FC, FormEvent} from 'react';
import {useParams} from 'react-router-dom';
import moment from 'moment';
import {
  Alert,
  Avatar,
  Box,
  Button,
  DatePicker,
  Paper,
  Progress,
  Stack,
  TextField,
  Typography,
} from '@wellstone-solutions/web';
import {getInvite, registerInvite} from '../api';
import {APP_STORE_URL, PLAY_STORE_URL} from '../constants';
import type {InviteDetails} from '../types';

type Status = 'loading' | 'ready' | 'unavailable' | 'success';

// Lightweight client-side check so we can disable the button and give immediate
// feedback. The backend remains the source of truth (handled via the 400 path).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Minimum self-registration age should be 18
const MIN_AGE = 18;

const isEligibleAge = (birthdate: string): boolean =>
  birthdate !== '' &&
  moment(birthdate, 'YYYY-MM-DD', true).isValid() &&
  moment(birthdate).isSameOrBefore(moment().subtract(MIN_AGE, 'years'));

const getErrorStatus = (error: unknown): number | undefined => {
  if (typeof error === 'object' && error !== null) {
    const e = error as {response?: {status?: number}; status?: number};
    return e.response?.status ?? e.status;
  }
  return undefined;
};

const getEmailFieldError = (error: unknown): string | undefined => {
  if (typeof error === 'object' && error !== null) {
    const data = (error as {response?: {data?: {email?: unknown}}}).response
      ?.data;
    const emailErrors = data?.email;
    if (Array.isArray(emailErrors) && typeof emailErrors[0] === 'string') {
      return emailErrors[0];
    }
  }
  return undefined;
};

export const JoinModule: FC = () => {
  const {token} = useParams();
  const [status, setStatus] = useState<Status>('loading');
  const [invite, setInvite] = useState<InviteDetails | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    if (!token) {
      setStatus('unavailable');
      return;
    }

    getInvite(token)
      .then((data) => {
        if (active) {
          setInvite(data);
          setStatus('ready');
        }
      })
      .catch(() => {
        // All load failures (404 / expired / used up / network) collapse to the
        // same generic screen — we never reveal why a link is invalid.
        if (active) {
          setStatus('unavailable');
        }
      });

    return () => {
      active = false;
    };
  }, [token]);

  // Standalone page — keep the Bridge product name out of the browser tab too.
  useEffect(() => {
    const previousTitle = document.title;
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    document.title = invite ? `Join ${invite.organization.name}` : 'Pathfinder';
  }, [invite]);

  const isValidEmail = EMAIL_REGEX.test(email.trim());
  const isAdult = isEligibleAge(birthdate);
  // Only surface the age error once a date has been selected
  const ageError =
    birthdate !== '' && !isAdult
      ? `You must be ${MIN_AGE} or older to register.`
      : '';

  const handleSubmit = async (): Promise<void> => {
    if (isSubmitting || !token || !isValidEmail || !isAdult) {
      return;
    }

    setFieldError(null);
    setFormError(null);
    setIsSubmitting(true);

    try {
      await registerInvite(token, {
        email: email.trim(),
        ...(name.trim() ? {name: name.trim()} : {}),
      });
      setStatus('success');
    } catch (error) {
      const errorStatus = getErrorStatus(error);

      if (errorStatus === 400) {
        setFieldError(
          getEmailFieldError(error) ?? 'Enter a valid email address.',
        );
      } else if (errorStatus === 404) {
        setStatus('unavailable');
      } else if (errorStatus === 429) {
        setFormError('Too many attempts, please try again in a minute.');
      } else {
        setFormError('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    void handleSubmit();
  };

  return (
    <Box sx={styles.page}>
      <Paper sx={styles.card}>
        {status === 'loading' && (
          <Stack alignItems="center" sx={{py: 6}}>
            <Progress aria-label="Loading invite" />
          </Stack>
        )}

        {status === 'unavailable' && (
          <Stack spacing={2} alignItems="center" sx={{textAlign: 'center'}}>
            <Typography variant="h5" sx={styles.heading}>
              This invite link is no longer available
            </Typography>
            <Typography color="text.secondary">
              Please ask your organization for a new one.
            </Typography>
          </Stack>
        )}

        {status === 'success' && (
          <Stack spacing={2} sx={{textAlign: 'center'}} alignItems="center">
            <Typography variant="h5" sx={styles.heading}>
              Check your email
            </Typography>
            <Typography>
              We sent a 6-digit login code to <strong>{email.trim()}</strong>.
            </Typography>
            <Typography color="text.secondary">
              Download Pathfinder Companion and log in with that code.
            </Typography>
            <Stack
              direction={{xs: 'column', sm: 'row'}}
              spacing={2}
              sx={{width: '100%', pt: 1}}>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{flex: 1, textDecoration: 'none'}}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  sx={styles.ctaButton}>
                  App Store
                </Button>
              </a>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{flex: 1, textDecoration: 'none'}}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  sx={styles.ctaButton}>
                  Google Play
                </Button>
              </a>
            </Stack>
          </Stack>
        )}

        {status === 'ready' && invite && (
          <Stack spacing={3}>
            <Stack spacing={1.5} alignItems="center" sx={{textAlign: 'center'}}>
              {invite.organization.imageUrl && (
                <Avatar
                  src={invite.organization.imageUrl}
                  alt={invite.organization.name}
                  sx={{width: 72, height: 72}}
                />
              )}
              <Typography variant="h5" sx={styles.heading}>
                Join {invite.group.name} at {invite.organization.name}
              </Typography>
              <Typography color="text.secondary">
                Enter your email and we'll send a login code to get started.
              </Typography>
            </Stack>

            {/* Real <form> so the mobile keyboard shows a "Go"/submit action. */}
            <form onSubmit={handleFormSubmit} noValidate>
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  type="email"
                  required
                  fullWidth
                  autoFocus
                  autoComplete="email"
                  // Mobile keyboard hygiene: email keys, no auto-capitalize/correct.
                  inputProps={{
                    inputMode: 'email',
                    autoCapitalize: 'none',
                    autoCorrect: 'off',
                    spellCheck: false,
                  }}
                  value={email}
                  error={Boolean(fieldError)}
                  helperText={fieldError ?? ''}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (fieldError) {
                      setFieldError(null);
                    }
                  }}
                />
                <DatePicker
                  label="Date of birth"
                  fieldName="birthdate"
                  value={birthdate}
                  error={ageError}
                  onChange={(date) => {
                    setBirthdate(
                      date?.isValid() ? date.format('YYYY-MM-DD') : '',
                    );
                  }}
                />
                <TextField
                  label="Name (optional)"
                  fullWidth
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />

                {formError && <Alert severity="error">{formError}</Alert>}

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  sx={styles.ctaButton}
                  disabled={!isValidEmail || !isAdult || isSubmitting}>
                  {isSubmitting ? (
                    <Progress
                      progressType="circular"
                      size={24}
                      sx={{color: 'inherit'}}
                      aria-label="Submitting"
                    />
                  ) : (
                    'Continue'
                  )}
                </Button>
              </Stack>
            </form>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

// The invite route hides the Bridge SiteHeader (see App.js), so this page is
// standalone — it just centers a content-height card in the available space.
const styles = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    width: '100%',
    boxSizing: 'border-box',
    py: 4,
    px: 2,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    boxSizing: 'border-box',
    p: {xs: 3, sm: 4},
    borderRadius: 2,
  },
  heading: {
    fontSize: {xs: '1.375rem', sm: '1.5rem'},
    fontWeight: 700,
  },
  // ~48px min keeps primary actions a comfortable mobile tap target.
  ctaButton: {
    minHeight: 48,
  },
};
